import React, { Dispatch, SetStateAction } from 'react'
import './TacticalDetection.scss'

import { Grid, MenuItem, Select } from '@material-ui/core'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { useSelector } from 'react-redux'

import grayUavImage from '../../../assets/images/gray-uav-icon-small.svg'
import grayOtherImage from '../../../assets/images/gray-other-icon-small.svg'
import orangeUavImage from '../../../assets/images/orange-unknown-icon-small.svg'
import entityFriendly from '../../../assets/images/entity-friendly.svg'
import entityUncertain from '../../../assets/images/entity-uncertain.svg'
import entitySuspect from '../../../assets/images/entity-suspect.svg'
import entityUnclassified from '../../../assets/images/entity-unclassified.svg'
import { threat_level } from '../../../config/constant'
import { setThreatLevel } from '../../../services/mission.service'
import { Feature } from 'src/redux/reducers/types'
import { Asset } from 'src/redux/reducers/assets'
import { AppState } from 'src/redux/store'

import cargoVessel from '../../../assets/images/cargo-vessel.jpg'
import fishingVessel from '../../../assets/images/fishing-vessel.jpg'
import floatingPowerStation from '../../../assets/images/floating-power-station.jpg'
import frigate from '../../../assets/images/frigate.jpg'
import highSpeedVessel from '../../../assets/images/high-speed-vessel.jpg'
import hospitalShip from '../../../assets/images/hospital-ship.jpg'
import militaryVessel from '../../../assets/images/military-vessel.jpg'
import pleasureBoat from '../../../assets/images/pleasure-boat.jpg'
import tanker from '../../../assets/images/tanker.jpg'
import unknowVessel from '../../../assets/images/unknow-vessel.jpg'

interface TacticalDectectionProps {
    setDetectionSelectedFromList: Dispatch<SetStateAction<any>>
    activeSessionId: null | number
}

function TacticalDetection(props: TacticalDectectionProps) {
    const setDetectionSelectedFromList = props.setDetectionSelectedFromList
    const activeSessionId = props.activeSessionId
    const detectionsList = useSelector((state: AppState) => state.assets.detectedList)
    const selectedDetection = useSelector((state: AppState) => state.assets.detectedSelectedAsset)

    function renderDetectionListImage(alliance: string, type: string, threat_level: string) {
        switch (alliance) {
            case 'Enemy':
                switch (threat_level) {
                    case 'Unclassified':
                        return (
                            <>
                                <Box
                                    component="img"
                                    sx={{ height: 15, width: 15, 'margin-right': 15, 'margin-top': 5 }}
                                    src={entityUnclassified as unknown as string}
                                />
                            </>
                        )
                    case 'Friendly':
                        return (
                            <>
                                <Box
                                    component="img"
                                    sx={{ height: 15, width: 15, 'margin-right': 15, 'margin-top': 5 }}
                                    src={entityFriendly as unknown as string}
                                />
                            </>
                        )
                    case 'Uncertain':
                        return (
                            <>
                                <Box
                                    component="img"
                                    sx={{ height: 15, width: 15, 'margin-right': 15, 'margin-top': 5 }}
                                    src={entityUncertain as unknown as string}
                                />
                            </>
                        )
                    case 'Suspect':
                        return (
                            <>
                                <Box
                                    component="img"
                                    sx={{ height: 15, width: 15, 'margin-right': 15, 'margin-top': 5 }}
                                    src={entitySuspect as unknown as string}
                                />
                            </>
                        )
                    default:
                        return (
                            <>
                                <Box
                                    component="img"
                                    sx={{ height: 15, width: 15, 'margin-right': 15, 'margin-top': 5 }}
                                    src={entityUnclassified as unknown as string}
                                />
                            </>
                        )
                }
            case 'Neutral':
                if (type == 'Drone') {
                    return (
                        <>
                            <Box
                                component="img"
                                sx={{ height: 15, width: 15, 'margin-right': 25, 'margin-top': 5 }}
                                src={grayUavImage as unknown as string}
                            />
                        </>
                    )
                } else {
                    return (
                        <>
                            <Box
                                component="img"
                                sx={{ height: 15, width: 15, 'margin-right': 25, 'margin-top': 5 }}
                                src={grayOtherImage as unknown as string}
                            />
                        </>
                    )
                }
            default:
                return (
                    <>
                        <Box
                            component="img"
                            sx={{ height: 15, width: 15, 'margin-right': 25, 'margin-top': 5 }}
                            src={orangeUavImage as unknown as string}
                        />
                    </>
                )
        }
    }

    function compareNames(a: Asset, b: Asset) {
        if (a.name < b.name) return -1

        if (a.name > b.name) return 1

        return 0
    }

    // @ts-ignore - Could potentially be re-enabled later? (ST 2025-05-23)
    function renderDetectionList() {
        if (detectionsList != null && detectionsList !== undefined && detectionsList.length > 0) {
            return (
                <>
                    {[...detectionsList]?.sort(compareNames).map((detectedElement, index) => (
                        <div className="ListDetectedElement" key={index} onClick={(e) => handleItemClick(e as any, detectedElement.name)}>
                            {renderDetectionListImage(detectedElement.alliance, detectedElement.type, detectedElement.threat_level)}
                            <span>{detectedElement.name}</span>
                        </div>
                    ))}
                </>
            )
        }
    }

    function selectHighlightedItem(selection_name: string) {
        let options = document.querySelectorAll('.ListDetectedElement') as any

        for (let i = 0; i < options.length; i++) {
            if (options[i].innerText.includes(selection_name)) {
                options[i].style.backgroundColor = '#CCF2F8'
            } else {
                options[i].style.backgroundColor = ''
            }
        }
    }

    function renderFeature(feature: Feature) {
        return (
            <>
                <Grid item xs={6} sm={6} key={`${feature.name}-1`}>
                    <span className="TelemetryItemTitle">{feature.name}:</span>
                </Grid>
                <Grid item xs={6} sm={6} key={`${feature.name}-2`}>
                    {feature.state}
                </Grid>
            </>
        )
    }

    function renderRecommendedThreatLevel(feature: Feature) {
        return (
            <>
                <Grid item xs={6} sm={6} key={`${feature.name}-1`}>
                    <span className="TelemetryItemTitle">{'Recommendation'}:</span>
                </Grid>
                <Grid item xs={6} sm={6} key={`${feature.name}-2`}>
                    {feature.state}
                </Grid>
            </>
        )
    }

    function renderEntityPicture(model: string) {
        let image = <img src={unknowVessel} className="PictureContainer" />

        switch (model) {
            case 'Cargo Vessel': {
                image = <img src={cargoVessel} className="PictureContainer" />
                break
            }
            case 'Fishing Vessel': {
                image = <img src={fishingVessel} className="PictureContainer" />
                break
            }
            case 'Floating Power Station': {
                image = <img src={floatingPowerStation} className="PictureContainer" />
                break
            }
            case 'Frigate': {
                image = <img src={frigate} className="PictureContainer" />
                break
            }
            case 'High-Speed Vessel': {
                image = <img src={highSpeedVessel} className="PictureContainer" />
                break
            }
            case 'Hospital Ship': {
                image = <img src={hospitalShip} className="PictureContainer" />
                break
            }
            case 'Military Vessel': {
                image = <img src={militaryVessel} className="PictureContainer" />
                break
            }
            case 'Pleasure Boat': {
                image = <img src={pleasureBoat} className="PictureContainer" />
                break
            }
            case 'Tanker': {
                image = <img src={tanker} className="PictureContainer" />
                break
            }
        }

        return (
            <>
                {image}
                <Typography className="PictureLegend" variant="h6" gutterBottom component="div">
                    {model}
                </Typography>
            </>
        )
    }

    function renderDetails() {
        if (selectedDetection.name != '') {
            selectHighlightedItem(selectedDetection.name)

            return (
                <>
                    <Typography className="TitleContainer" variant="h6" gutterBottom component="div">
                        {selectedDetection.name}
                    </Typography>
                    {renderEntityPicture(selectedDetection.visible ? selectedDetection.model : 'Unknow')}
                    <ul className="tabs has-divider w-full fluid">
                        <li className="tab active">
                            <span>Telemetry</span>
                        </li>
                    </ul>
                    <Grid className="ItemsGrid" container spacing={1}>
                        {selectedDetection.features.map((feature: Feature) => {
                            if (feature.name != 'Recommended threat level') return renderFeature(feature)
                        })}
                        <Grid item xs={12} sm={12}>
                            <span className="TelemetryItemTitle"></span>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <span className="TelemetryItemTitle">Latitude:</span>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            {((selectedDetection.latitude as number) ?? selectedDetection.waypoints[0].latitude).toFixed(6)}
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <span className="TelemetryItemTitle">Longitude:</span>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            {((selectedDetection.longitude as number) ?? selectedDetection.waypoints[0].longitude).toFixed(6)}
                        </Grid>
                        {/*	
                        <Grid item xs={6} sm={6}>
                            <span className="TelemetryItemTitle">Altitude (AGL):</span>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            {((selectedDetection.altitude as number) ?? selectedDetection.waypoints[0].altitude).toFixed(1)} m
                        </Grid>
						*/}
                        <Grid item xs={6} sm={6}>
                            <span className="TelemetryItemTitle">Velocity:</span>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            {Math.round((selectedDetection.velocity as number) ?? selectedDetection.waypoints[0].velocity)} m/s
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <span className="TelemetryItemTitle"></span>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <span className="TelemetryItemTitle">Threat Level:</span>
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <Select
                                label="Threat Level"
                                value={selectedDetection['threat_level']}
                                onChange={updateThreatLevel}
                                MenuProps={{
                                    anchorOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    },
                                    getContentAnchorEl: null,
                                }}
                            >
                                {Object.keys(threat_level).map((threatLevelKey) => (
                                    // @ts-ignore - Should be OK; values come from the keys
                                    <MenuItem value={threat_level[threatLevelKey]} key={threatLevelKey}>
                                        {/* @ts-ignore - Should be OK; values come from the keys */}
                                        {threat_level[threatLevelKey]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        {selectedDetection.features.map((feature: Feature) => {
                            if (feature.name == 'Recommended threat level') return renderRecommendedThreatLevel(feature)
                        })}
                    </Grid>
                </>
            )
        } else {
            return (
                <Typography className="DetailsTitle" variant="h6" gutterBottom component="div">
                    No detection selected
                </Typography>
            )
        }
    }

    function handleItemClick(event: React.MouseEvent<any>, detectedName: string) {
        event.preventDefault()

        setDetectionSelectedFromList(detectedName)
        selectHighlightedItem(detectedName)
    }

    function updateThreatLevel(event: React.ChangeEvent<any>) {
        event.preventDefault()

        setThreatLevel(activeSessionId!, selectedDetection.id as unknown as number, event.target.value)
    }

    return (
        <div className="TacticalDetection" data-test-id="tactical-detection-container">
            {
                //<div className='TitleContainer'>
                //    <ul className="tabs has-divider w-full fluid">
                //        <li className="tab active">
                //            <span>Detected</span>
                //        </li>
                //    </ul>
                //</div>
                //<div className='ListContainer'>
                //    {renderDetectionList()}
                //</div>
            }
            <div className="DetailsContainer">{renderDetails()}</div>
        </div>
    )
}

export default TacticalDetection
